import { useToggle } from "@react-hookz/web";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";
import { FollowButtonOutline, FollowButtonVariant } from "scmp-sdk";

import { author as authorList } from "shared/data";
import { isValidUrl } from "shared/lib/utils";

import type { authorHeaderAuthor$key } from "scmp-app/queries/__generated__/authorHeaderAuthor.graphql";

import {
  Avatar,
  BasicInformationSubGrid,
  Container,
  DownTriangle,
  Education,
  Expertise,
  ExtraInformationItemLabel,
  Follow,
  Language,
  Location,
  LocationAndSocialSubGrid,
  MobileBio,
  MobileMoreInformation,
  MoreButton,
  Name,
  Role,
  SocialLinks,
  StyledSCMPLabLogo,
  TabletUpBio,
  TabletUpMoreInformation,
  TwitterLink,
  UpTriangle,
} from "./styles";

type Props = {
  reference: authorHeaderAuthor$key;
};

export const AuthorHeader: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const author = useFragment(
    graphql`
      fragment authorHeaderAuthor on Author {
        entityId
        name
        location
        socialLinks {
          class
          url
        }
        role {
          text
        }
        bio
        education
        areaOfExpertise
        languageSpoken
        ...entityFollowButtonBase
        ...authorAvatarAuthor
        ...authorSocialLinksAuthor
      }
    `,
    reference_,
  );

  const isCurationTeam = author.entityId === authorList.curationTeam.entityId;

  const renderFollowButton = () => (
    <Follow
      outline={FollowButtonOutline.Outlined}
      reference={author}
      source="Author"
      variant={FollowButtonVariant.Light}
    />
  );

  const renderTwitterLink = () => {
    const twitterLink = author.socialLinks?.find(item => item?.class === "twitter")?.url;
    if (!twitterLink || !isValidUrl(twitterLink)) return;

    const twitterName = `@${new URL(twitterLink).pathname.slice(1)}`;
    return (
      <TwitterLink href={twitterLink} target="_blank">
        {twitterName}
      </TwitterLink>
    );
  };

  const [isShowMore, toggleShowMore] = useToggle(false);

  const renderBio = () => {
    if (!author.bio) return;
    const delimitedBio = author.bio.split(" ");

    const needTrim = delimitedBio.length > 120;
    const shouldTrim = !isShowMore && needTrim;
    const trimmedBio = `${author.bio.split(" ").slice(0, 120).join(" ")}...`;

    return {
      component: (
        <>
          <MobileBio>{author.bio}</MobileBio>
          <TabletUpBio>{shouldTrim ? trimmedBio : author.bio}</TabletUpBio>
        </>
      ),
      needTrim,
    };
  };
  const bio = renderBio();

  const hasMore =
    !!bio?.needTrim || !!author.areaOfExpertise?.length || !!author.languageSpoken?.length;

  const renderShowMoreButton = () => {
    if (!hasMore || isCurationTeam) return null;

    return (
      <MoreButton onClick={toggleShowMore} text>
        {isShowMore ? (
          <>
            Show less <UpTriangle />
          </>
        ) : (
          <>
            Show more <DownTriangle />
          </>
        )}
      </MoreButton>
    );
  };

  const renderMoreInformation = () => {
    if (!hasMore || isCurationTeam) return null;

    return (
      <>
        {author.education && (
          <Education>
            <ExtraInformationItemLabel>Education:</ExtraInformationItemLabel>
            {author.education}
          </Education>
        )}
        {!!author.areaOfExpertise?.length && (
          <Expertise>
            <ExtraInformationItemLabel>Areas of Expertise:</ExtraInformationItemLabel>
            {author.areaOfExpertise.join(", ")}
          </Expertise>
        )}
        {!!author.languageSpoken?.length && (
          <Language>
            <ExtraInformationItemLabel>Languages Spoken:</ExtraInformationItemLabel>{" "}
            {author.languageSpoken.join(", ")}
          </Language>
        )}
      </>
    );
  };

  return (
    <Container>
      <Avatar height={320} reference={author} width={320} withoutLink />
      <Name>{author.name}</Name>
      {renderFollowButton()}
      {isCurationTeam && <StyledSCMPLabLogo />}
      <LocationAndSocialSubGrid>
        {author.location && <Location> {author.location}</Location>}
        {renderTwitterLink()}
        <SocialLinks reference={author} />
      </LocationAndSocialSubGrid>
      <BasicInformationSubGrid>
        {author.role?.text && <Role>{author.role.text}</Role>}
        {bio?.component}
      </BasicInformationSubGrid>
      <MobileMoreInformation>{renderMoreInformation()}</MobileMoreInformation>
      <TabletUpMoreInformation>{isShowMore && renderMoreInformation()}</TabletUpMoreInformation>
      {renderShowMoreButton()}
    </Container>
  );
};

AuthorHeader.displayName = "AuthorHeader";
